/* eslint-disable import/max-dependencies */
import React, { lazy } from 'react';
import { Container, Row, Text } from '@paypalcorp/pp-react';
import { Message } from '@paypalcorp/worldready-react';
import { css, Global } from '@emotion/core';
import serverData from 'utils/server-data';
import { Route, Routes, useLocation } from 'react-router';

const ErrorDisplayHOC = lazy(() => import('../Common/withErrorDisplay'));
const NationalityScreen = lazy(() => import('./pages/Nationality'));
const BusinessType = lazy(() => import('./pages/BusinessType'));
const BusinessInfo = lazy(() => import('./pages/BusinessInfo'));
const BusinessName = lazy(() => import('./pages/BusinessName'));
const Checkpoint = lazy(() => import('./pages/Checkpoint'));
const SameAddressCheck = lazy(() => import('./pages/SameAddressCheck'));
const BusinessAddress = lazy(() => import('./pages/BusinessAddress'));
const HomeAddress = lazy(() => import('./pages/HomeAddress'));
const SignUp = lazy(() => import('./pages/SignUp'));
const DateOfBirth = lazy(() => import('./pages/DateOfBirth'));
const USCitizenship = lazy(() => import('./pages/USCitizenship'));
const Ssn = lazy(() => import('./pages/SSN'));
const BackupWithholding = lazy(() => import('./pages/BackupWithholding'));
const AverageVolume = lazy(() => import('./pages/AverageVolume'));
const CountryOfIncorporation = lazy(() =>
    import('./pages/CountryOfIncorporation')
);
const Ein = lazy(() => import('./pages/EIN'));
const PhoneNumber = lazy(() => import('./pages/PhoneNumber'));
const MCC = lazy(() => import('./pages/MCC'));
const PhoneOTP = lazy(() => import('./pages/PhoneOTP'));
const CompanyRegistrationNumber = lazy(() =>
    import('./pages/CompanyRegistrationNumber')
);
const Review = lazy(() => import('./pages/Review'));
const Decline = lazy(() => import('./pages/decisions/Decline'));
const NotSupported = lazy(() => import('./pages/unsupportedCases/Unsupported'));
const NotRegistered = lazy(() =>
    import('./pages/unsupportedCases/NotRegistered')
);
const RegionNotSupported = lazy(() =>
    import('./pages/unsupportedCases/RegionNotSupported')
);
const MccNotSupported = lazy(() =>
    import('./pages/unsupportedCases/MccNotSupported')
);
const RiskReview = lazy(() => import('./pages/decisions/Review'));
const NeedMoreInfo = lazy(() => import('./pages/decisions/NeedMoreData'));
const SanctionsDecision = lazy(() =>
    import('./pages/decisions/SanctionsDecision')
);
const AuthorizedRepresentative = lazy(() =>
    import('./pages/AuthorizedRepresentative')
);
const BeneficiaryCheck = lazy(() => import('./pages/BeneficiaryCheck'));
const BeneficialOwner = lazy(() => import('./pages/BeneficialOwner'));
const CompanyExecutive = lazy(() => import('./pages/companyExecutive'));
const PartnershipRegistrationInfo = lazy(() =>
    import('./pages/PartnershipRegistrationInfo')
);
const NonProfitRegistry = lazy(() => import('./pages/NonProfitRegistry'));
const ConsumerUpgrade = lazy(() => import('./pages/ConsumerUpgrade'));
const Upgrade = lazy(() => import('./pages/Upgrade'));
const BusinessPhoneNumber = lazy(() => import('./pages/BusinessPhoneNumber'));
const Regularity = lazy(() => import('./pages/Regularity'));
const Location = lazy(() => import('./pages/Location'));
const AverageTransaction = lazy(() => import('./pages/AverageTransaction'));
const CardTurnover = lazy(() => import('./pages/CardTurnover'));
const ExpectedTurnover = lazy(() => import('./pages/ExpectedTurnover'));
const CurrentAnnualTurnover = lazy(() =>
    import('./pages/CurrentAnnualTurnover')
);
const BusinessRegistered = lazy(() => import('./pages/BusinessRegistered'));
const BackupWithholdingLegalEntity = lazy(() =>
    import('./pages/BackupWithholdingLegalEntity')
);

const base = css`
    padding-top: 1.5rem;
    width: 32.5rem;
`;

const mobile = css`
    padding-top: 1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: 100%;
`;

const renderUnsupportedScreen = (property) => {
    if (property === 'mcc') {
        return <MccNotSupported />;
    }

    return <NotSupported />;
};

export default function GBRoutes() {
    const notSupported = serverData.unsupport;
    const unsupportedProperty = serverData?.unsupportedPage?.property;
    const location = useLocation();

    return (
        <>
            <Global
                styles={css`
                    .standard-font-size {
                        font-size: 1rem;
                    }
                    .margin-top-1rem {
                        margin-top: 1rem;
                    }
                    .bg-white {
                        background: white;
                    }
                    .flex-no-wrap {
                        flex-wrap: nowrap;
                    }
                    .column-gap-10 {
                        column-gap: 10px;
                    }
                    .progress-bar-width {
                        width: ${serverData.isNativeWeb ? '100%' : '90%'};
                        margin: auto;
                    }
                `}
            />
            <Container
                data-testid="app-container"
                css={css`
                    ${serverData.isNativeWeb ? mobile : base}
                `}>
                <Row align="center">
                    <React.Suspense
                        fallback={
                            <Text>
                                <Message id="app.suspense.fallbackText" />
                            </Text>
                        }>
                        {notSupported ? (
                            // Show error screen based on conditions.
                            // Currently differentiates between MccNotSupported and General Errors
                            renderUnsupportedScreen(unsupportedProperty)
                        ) : (
                            <ErrorDisplayHOC
                                location={location}
                                style={{ flex: '100%' }}>
                                <Routes>
                                    <Route
                                        path="/signUp"
                                        element={<SignUp />}
                                    />
                                    <Route
                                        path="/businessType"
                                        element={<BusinessType />}
                                    />
                                    <Route
                                        path="/businessRegistered"
                                        element={<BusinessRegistered />}
                                    />
                                    <Route
                                        path="/businessInfo"
                                        element={<BusinessInfo />}
                                    />
                                    <Route
                                        path="/nationality"
                                        element={<NationalityScreen />}
                                    />
                                    <Route
                                        path="/businessName"
                                        element={<BusinessName />}
                                    />
                                    <Route
                                        path="/companyRegistrationNumber"
                                        element={<CompanyRegistrationNumber />}
                                    />
                                    <Route
                                        path="/checkpoint"
                                        element={<Checkpoint />}
                                    />
                                    <Route
                                        path="/sameAddressCheck"
                                        element={<SameAddressCheck />}
                                    />
                                    <Route
                                        path="/businessAddress"
                                        element={<BusinessAddress />}
                                    />
                                    <Route
                                        path="/homeAddress"
                                        element={<HomeAddress />}
                                    />
                                    <Route
                                        path="/averageVolume"
                                        element={<AverageVolume />}
                                    />
                                    <Route
                                        path="/countryOfIncorporation"
                                        element={<CountryOfIncorporation />}
                                    />
                                    <Route path="/ein" element={<Ein />} />
                                    <Route
                                        path="/backupWithholdingLegalEntity"
                                        element={
                                            <BackupWithholdingLegalEntity />
                                        }
                                    />
                                    <Route
                                        path="/phoneNumber"
                                        element={<PhoneNumber />}
                                    />
                                    <Route
                                        path="/phoneOTP"
                                        element={<PhoneOTP />}
                                    />
                                    <Route
                                        path="/dateOfBirth"
                                        element={<DateOfBirth />}
                                    />
                                    <Route
                                        path="/usCitizenship"
                                        element={<USCitizenship />}
                                    />
                                    <Route path="/ssn" element={<Ssn />} />
                                    <Route
                                        path="/mcc"
                                        /* eslint-disable-next-line react/jsx-pascal-case */
                                        element={<MCC />}
                                    />
                                    <Route
                                        path="/backupWithholding"
                                        element={<BackupWithholding />}
                                    />
                                    <Route
                                        path="/review"
                                        element={<Review />}
                                    />
                                    <Route
                                        path="/notRegistered"
                                        element={<NotRegistered />}
                                    />
                                    <Route
                                        path="/regionNotSupported"
                                        element={<RegionNotSupported />}
                                    />
                                    <Route
                                        path="/authorizedRepresentative"
                                        element={<AuthorizedRepresentative />}
                                    />
                                    <Route
                                        path="/beneficialOwner"
                                        element={<BeneficialOwner />}
                                    />
                                    <Route
                                        path="/beneficiaryCheck"
                                        element={<BeneficiaryCheck />}
                                    />
                                    <Route
                                        path="/companyExecutive"
                                        element={<CompanyExecutive />}
                                    />
                                    <Route
                                        path="/partnershipRegistrationInfo"
                                        element={
                                            <PartnershipRegistrationInfo />
                                        }
                                    />
                                    <Route
                                        path="/decline"
                                        element={<Decline />}
                                    />
                                    <Route
                                        path="/riskReview"
                                        element={<RiskReview />}
                                    />
                                    <Route
                                        path="/needMoreInfo"
                                        element={<NeedMoreInfo />}
                                    />
                                    <Route
                                        path="/sanctionsDecision"
                                        element={<SanctionsDecision />}
                                    />
                                    <Route
                                        path="/nonProfitRegistry"
                                        element={<NonProfitRegistry />}
                                    />
                                    <Route
                                        path="/consumerUpgrade"
                                        element={<ConsumerUpgrade />}
                                    />
                                    <Route
                                        path="/upgrade"
                                        element={<Upgrade />}
                                    />
                                    <Route
                                        path="/businessPhoneNumber"
                                        element={<BusinessPhoneNumber />}
                                    />
                                    <Route
                                        path="/location"
                                        element={<Location />}
                                    />
                                    <Route
                                        path="/regularity"
                                        element={<Regularity />}
                                    />
                                    <Route
                                        path="/averageTransaction"
                                        element={<AverageTransaction />}
                                    />
                                    <Route
                                        path="/cardTurnover"
                                        element={<CardTurnover />}
                                    />
                                    <Route
                                        path="/expectedTurnover"
                                        element={<ExpectedTurnover />}
                                    />
                                    <Route
                                        path="/currentAnnualTurnover"
                                        element={<CurrentAnnualTurnover />}
                                    />
                                    <Route
                                        path="*"
                                        element={<NotSupported />}
                                    />
                                </Routes>
                            </ErrorDisplayHOC>
                        )}
                    </React.Suspense>
                </Row>
            </Container>
        </>
    );
}
